import { PreviewTrust } from "@/schema"
import { ButtonBadge } from "@ui/button-badge"
import { Badge } from "@ui/shad"
import { ExternalLink, User } from "lucide-react"

import { usePeekViewData } from "@/hooks/api/peek-views/queries"
import { Link } from "@/components/book-link"
import { ContactInfoBadge } from "@/components/tables/contact-info-badge"

import PeekViewSkeleton from "./skeleton"

export default function TrustPeekView({ trustId, href }: { trustId: string; href: string | null }) {
  const { data: trust, isLoading } = usePeekViewData(trustId, "Trust") as {
    data: PreviewTrust
    isLoading: boolean
  }

  if (isLoading) return <PeekViewSkeleton />

  const household = trust.household

  return (
    <div className="flex w-xs flex-col gap-2 xl:w-sm">
      <div className="flex items-center justify-between gap-2">
        <div className="flex min-w-0 items-baseline">
          <p className="truncate font-medium">{trust.name}</p>
          {href && (
            <Link
              href={href}
              className="text-muted-foreground hover:text-link ml-2 transition-colors"
              onClick={(e) => e.stopPropagation()}
              aria-label="Open in new page"
            >
              <ExternalLink className="size-3.5" strokeWidth={1.5} />
            </Link>
          )}
        </div>
        <Badge variant="outline" className="flex-none">
          Trust
        </Badge>
      </div>
      {trust.additional_info && <p className="text-muted-foreground text-sm">{trust.additional_info}</p>}
      {trust.tax_id && (
        <div className="text-muted-foreground text-xs font-medium">
          Tax ID <span className="text-foreground">{trust.tax_id}</span>
        </div>
      )}
      {household && (
        <div className="flex flex-col gap-1 text-sm">
          <div className="flex items-center gap-2">
            <User className="text-icon h-4 w-4" />
            <span className="font-medium">{household.name}</span>
            {household.type && (
              <Badge variant="outline" className="text-xs capitalize">
                {household.type.toLowerCase()}
              </Badge>
            )}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {household.primary_email && (
              <ButtonBadge text={household.primary_email} copyText={household.primary_email} />
            )}
            {household.primary_phone && (
              <ContactInfoBadge value={household.primary_phone} extension={household.primary_phone_extension} />
            )}
          </div>
        </div>
      )}
    </div>
  )
}
